"use client";

import { devLabel } from "@/lib/dev-labels";
import styles from "./NarrationPanel.module.css";
import type { NarrationMode, NarrationSubmode } from "./narration-data";

/**
 * ModeSelector — Mode A (원본 보존) / Mode B (스토리 재구성).
 *
 * Mockup source: docs/mockups/narration.html panel 23-A.
 * Mode A has 2 submodes (A-1 / A-2). Mode B has none.
 */

export interface ModeSelectorProps {
	mode: NarrationMode;
	submode: NarrationSubmode;
	onModeChange: (m: NarrationMode) => void;
	onSubmodeChange: (s: NarrationSubmode) => void;
}

export function ModeSelector({
	mode,
	submode,
	onModeChange,
	onSubmodeChange,
}: ModeSelectorProps) {
	return (
		<div
			className={styles.sideBlock}
			role="radiogroup"
			aria-label="나레이션 모드"
			data-testid="mode-selector"
			{...devLabel("23-A")}
		>
			<div className={styles.sideTitle}>나레이션 모드</div>

			{/* Mode A — 원본 보존 */}
			<div
				className={`${styles.modeCard} ${mode === "A" ? styles.modeCardOn : ""}`}
				role="radio"
				tabIndex={0}
				aria-checked={mode === "A"}
				data-testid="mode-a"
				onClick={() => onModeChange("A")}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onModeChange("A");
					}
				}}
				{...devLabel("23-A1")}
			>
				<div className={styles.modeCardHead}>
					<div className={styles.modeRadio} />
					<span className={styles.modeTitle}>Mode A — 원본 보존</span>
				</div>
				<div className={styles.modeDesc}>
					원본 음성 / 자막 유지 + 옵션 나레이션
				</div>
				{mode === "A" && (
					<>
						<button
							type="button"
							className={`${styles.submode} ${submode === "A-1" ? styles.submodeOn : ""}`}
							onClick={(e) => {
								e.stopPropagation();
								onSubmodeChange("A-1");
							}}
							data-testid="submode-a1"
							{...devLabel("23-A1a")}
						>
							<div className={styles.subRadio} />
							<span>A-1 · 원본 음성 + Whisper 자막</span>
						</button>
						<button
							type="button"
							className={`${styles.submode} ${submode === "A-2" ? styles.submodeOn : ""}`}
							onClick={(e) => {
								e.stopPropagation();
								onSubmodeChange("A-2");
							}}
							data-testid="submode-a2"
							{...devLabel("23-A1b")}
						>
							<div className={styles.subRadio} />
							<span>A-2 · 원본 자막 + 새 voice 더빙</span>
						</button>
					</>
				)}
			</div>

			{/* Mode B — 스토리 재구성 */}
			<div
				className={`${styles.modeCard} ${mode === "B" ? styles.modeCardOn : ""}`}
				role="radio"
				tabIndex={0}
				aria-checked={mode === "B"}
				data-testid="mode-b"
				onClick={() => onModeChange("B")}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onModeChange("B");
					}
				}}
				{...devLabel("23-A2")}
			>
				<div className={styles.modeCardHead}>
					<div className={styles.modeRadio} />
					<span className={styles.modeTitle}>Mode B — 스토리 재구성</span>
				</div>
				<div className={styles.modeDesc}>
					Claude 로 새 narration 스크립트 생성 · 12 테마 × 12 톤 × 6 voice
				</div>
			</div>
		</div>
	);
}
